import { getFollowers } from '../services/firestoreService';

/**
 * Check if a viewer can view a user's profile
 * @param {string} viewerId - ID of the user trying to view
 * @param {string} profileUserId - ID of the profile owner
 * @param {boolean} isPublic - Whether the profile is public
 * @param {boolean} isOwnProfile - Whether viewing own profile
 * @returns {Promise<boolean>} - Whether the viewer can access the profile
 */
export const canViewProfile = async (viewerId, profileUserId, isPublic, isOwnProfile = false) => {
  // Own profile is always viewable
  if (isOwnProfile || viewerId === profileUserId) {
    return true;
  }
  
  // Public profiles are viewable by all authenticated users
  if (isPublic) {
    return true;
  }
  
  // Private profiles require following relationship
  try {
    const { followers } = await getFollowers(profileUserId);
    const isFollower = followers.some(f => f.followerId === viewerId);
    return isFollower;
  } catch (error) {
    console.error('Error checking view permission:', error);
    return false;
  }
};

/**
 * Check if a viewer can view an activity item
 * @param {object} activityItem - The activity item
 * @param {string} viewerId - ID of the user trying to view
 * @param {boolean} isFollowing - Whether the viewer follows the activity owner
 * @returns {boolean} - Whether the viewer can see the activity
 */
export const canViewActivity = (activityItem, viewerId, isFollowing) => {
  // Own activities are always viewable
  if (activityItem.userId === viewerId) {
    return true;
  }
  
  // Public activities are viewable by all
  if (activityItem.isPublic) {
    return true;
  }
  
  // Private activities require following relationship
  return isFollowing;
};

/**
 * Check if a user can edit a profile
 * @param {string} userId - ID of the profile owner
 * @param {string} currentUserId - ID of the current user
 * @param {string} currentUserRole - Role of the current user
 * @returns {boolean} - Whether the user can edit the profile
 */
export const canEditProfile = (userId, currentUserId, currentUserRole = 'owner') => {
  // Users can only edit their own profile
  // Admins can also edit profiles (if needed for moderation)
  return userId === currentUserId || currentUserRole === 'admin' || currentUserRole === 'super_admin';
};

/**
 * Filter activities based on privacy settings
 * @param {Array} activities - Array of activity items
 * @param {string} viewerId - ID of the user viewing
 * @param {Array} followingIds - Array of user IDs that the viewer follows
 * @returns {Array} - Filtered activities that the viewer can see
 */
export const filterActivitiesByPrivacy = (activities, viewerId, followingIds = []) => {
  return activities.filter(activity => {
    // Own activities
    if (activity.userId === viewerId) {
      return true;
    }
    
    // Public activities
    if (activity.isPublic) {
      return true;
    }
    
    // Private activities - requires following
    return followingIds.includes(activity.userId);
  });
};

/**
 * Get display name for a user (alias or email/name fallback)
 * @param {object} user - User object
 * @param {object} profile - User profile object
 * @returns {string} - Display name
 */
export const getProfileDisplayName = (user, profile) => {
  if (profile?.alias) {
    return profile.alias;
  }
  
  if (user?.displayName) {
    return user.displayName;
  }
  
  if (user?.email) {
    return user.email.split('@')[0];
  }
  
  return 'Anonymous User';
};

/**
 * Format follower/following count for display
 * @param {number} count - The count
 * @returns {string} - Formatted count (e.g., "1.2K", "5.3M")
 */
export const formatFollowerCount = (count) => {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  
  return count.toString();
};

/**
 * Get time ago string for activity timestamps
 * @param {Date|object} timestamp - Firebase timestamp or Date object
 * @returns {string} - Human-readable time ago
 */
export const getTimeAgo = (timestamp) => {
  if (!timestamp) return 'Just now';
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  if (seconds < 2592000) return `${Math.floor(seconds / 604800)}w ago`;
  if (seconds < 31536000) return `${Math.floor(seconds / 2592000)}mo ago`;
  return `${Math.floor(seconds / 31536000)}y ago`;
};

/**
 * Get badge color based on badge type
 * @param {string} badgeType - Type of badge
 * @returns {string} - Tailwind color class
 */
export const getBadgeColor = (badgeType) => {
  const colorMap = {
    early_adopter: 'from-amber-500 to-orange-500',
    top_rated: 'from-yellow-400 to-amber-500',
    verified: 'from-blue-500 to-cyan-500',
    super_host: 'from-blue-600 to-sky-500',
    trusted_caregiver: 'from-cyan-500 to-blue-500',
    pet_lover: 'from-pink-500 to-rose-500',
    active_member: 'from-red-500 to-orange-500'
  };
  
  return colorMap[badgeType] || 'from-gray-500 to-gray-600';
};

/**
 * Get activity icon based on activity type
 * @param {string} activityType - Type of activity
 * @returns {string} - Icon name (Lucide React)
 */
export const getActivityIcon = (activityType) => {
  const iconMap = {
    booking_created: 'Calendar',
    booking_completed: 'CheckCircle',
    review_received: 'Star',
    badge_earned: 'Award',
    pet_added: 'Heart',
    follower_milestone: 'Users'
  };
  
  return iconMap[activityType] || 'Bell';
};

/**
 * Validate profile photo dimensions and size
 * @param {File} file - Image file
 * @param {string} type - Type of photo ('profile', 'cover', 'gallery')
 * @returns {Promise<{valid: boolean, error?: string, warning?: string}>}
 */
export const validateProfilePhoto = async (file, type = 'profile') => {
  // Check file size (max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return { valid: false, error: `File size is ${sizeMB}MB. Maximum allowed is 5MB. Please compress or choose a smaller image.` };
  }
  
  // Check file type - be specific about allowed formats
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type.toLowerCase())) {
    return { valid: false, error: 'Only JPG, PNG, GIF, or WebP images are supported' };
  }
  
  // Check dimensions based on type
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let minWidth, minHeight, recommendedWidth, recommendedHeight;
      
      switch (type) {
        case 'cover':
          minWidth = 1200;
          minHeight = 300;
          recommendedWidth = 1920;
          recommendedHeight = 480;
          break;
        case 'gallery':
          minWidth = 200;
          minHeight = 200;
          recommendedWidth = 800;
          recommendedHeight = 800;
          break;
        case 'profile':
        default:
          minWidth = 400;
          minHeight = 400;
          recommendedWidth = 800;
          recommendedHeight = 800;
          break;
      }
      
      if (img.width < minWidth || img.height < minHeight) {
        resolve({ 
          valid: false, 
          error: `Image is ${img.width}×${img.height}px. Minimum required is ${minWidth}×${minHeight}px. Please upload a larger image.` 
        });
      } else if (img.width < recommendedWidth || img.height < recommendedHeight) {
        resolve({ 
          valid: true, 
          warning: `Image will work but ${recommendedWidth}×${recommendedHeight}px or larger is recommended for best quality.` 
        });
      } else {
        resolve({ valid: true });
      }
      
      // Clean up object URL
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      resolve({ valid: false, error: 'Unable to read image file. The file may be corrupted or in an unsupported format.' });
    };
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Generate default profile colors based on user ID
 * @param {string} userId - User ID
 * @returns {object} - Object with gradient colors
 */
export const getDefaultProfileColors = (userId) => {
  const colors = [
    { from: '#667eea', to: '#764ba2' },
    { from: '#f093fb', to: '#f5576c' },
    { from: '#4facfe', to: '#00f2fe' },
    { from: '#43e97b', to: '#38f9d7' },
    { from: '#fa709a', to: '#fee140' },
    { from: '#30cfd0', to: '#330867' },
    { from: '#a8edea', to: '#fed6e3' },
    { from: '#ff9a9e', to: '#fecfef' }
  ];
  
  // Use user ID to consistently select a color
  const hash = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const index = hash % colors.length;
  
  return colors[index];
};

